import { computed } from 'vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { useMessageContext } from './provider.js';
import { MESSAGE_STATUS, MESSAGE_TYPES } from './constants';

// Share the existing channel/source/receipt rules between the timestamp and footer.
export function useMessageDeliveryStatus() {
  const {
    isAFacebookInbox,
    isALineChannel,
    isAPIInbox,
    isASmsInbox,
    isATelegramChannel,
    isATwilioChannel,
    isAWebWidgetInbox,
    isAWhatsAppChannel,
    isAnEmailChannel,
    isAnInstagramChannel,
    isATiktokChannel,
  } = useInbox();

  const {
    status,
    isPrivate,
    isWhatsAppHistoryEcho,
    sourceId,
    messageType,
    contentAttributes,
  } = useMessageContext();

  const showStatusIndicator = computed(() => {
    if (isPrivate.value || isWhatsAppHistoryEcho?.value) return false;
    // Don't show status for failed messages, we already show error message
    if (status.value === MESSAGE_STATUS.FAILED) return false;
    // Don't show status for deleted messages
    if (contentAttributes.value?.deleted) return false;

    if (messageType.value === MESSAGE_TYPES.OUTGOING) return true;
    if (messageType.value === MESSAGE_TYPES.TEMPLATE) return true;

    return false;
  });

  const isSent = computed(() => {
    if (!showStatusIndicator.value) return false;

    // Messages will be marked as sent for the Email channel if they have a source ID.
    if (isAnEmailChannel.value) return !!sourceId.value;

    if (
      isAWhatsAppChannel.value ||
      isATwilioChannel.value ||
      isAFacebookInbox.value ||
      isASmsInbox.value ||
      isATelegramChannel.value ||
      isAnInstagramChannel.value ||
      isATiktokChannel.value
    ) {
      return sourceId.value && status.value === MESSAGE_STATUS.SENT;
    }

    // API inbox messages use real sent/delivered/read status values from the external system.
    if (isAPIInbox.value) return status.value === MESSAGE_STATUS.SENT;

    // All messages will be mark as sent for the Line channel, as there is no source ID.
    if (isALineChannel.value) return true;

    return false;
  });

  const isDelivered = computed(() => {
    if (!showStatusIndicator.value) return false;

    if (
      isAWhatsAppChannel.value ||
      isATwilioChannel.value ||
      isASmsInbox.value ||
      isAFacebookInbox.value ||
      isAnInstagramChannel.value ||
      isATiktokChannel.value
    ) {
      return sourceId.value && status.value === MESSAGE_STATUS.DELIVERED;
    }
    // API inbox messages use real delivered status from the external system.
    if (isAPIInbox.value) return status.value === MESSAGE_STATUS.DELIVERED;
    // All messages marked as delivered for the web widget inbox once they are sent.
    if (isAWebWidgetInbox.value) {
      return status.value === MESSAGE_STATUS.SENT;
    }
    if (isALineChannel.value) {
      return status.value === MESSAGE_STATUS.DELIVERED;
    }

    return false;
  });

  const isRead = computed(() => {
    if (!showStatusIndicator.value) return false;

    if (
      isAWhatsAppChannel.value ||
      isATwilioChannel.value ||
      isAFacebookInbox.value ||
      isAnInstagramChannel.value ||
      isATiktokChannel.value
    ) {
      return sourceId.value && status.value === MESSAGE_STATUS.READ;
    }

    if (isAWebWidgetInbox.value || isAPIInbox.value) {
      return status.value === MESSAGE_STATUS.READ;
    }

    return false;
  });

  const statusToShow = computed(() => {
    if (status.value === MESSAGE_STATUS.UNKNOWN) return MESSAGE_STATUS.UNKNOWN;
    if (isRead.value) return MESSAGE_STATUS.READ;
    if (isDelivered.value) return MESSAGE_STATUS.DELIVERED;
    if (isSent.value) return MESSAGE_STATUS.SENT;

    return MESSAGE_STATUS.PROGRESS;
  });
  return { showStatusIndicator, statusToShow };
}
